<template>
    <modal id="customer-modal" :hide-footer="selectedUrl || !branchConfirmed" v-model="showModal" :loading="loading" :preloader="preloader"
        :title="generateModalTitle('customer')" size="extra-large" @submit="submitData">
        <form ref="form" :data-url='selectedUrl ? selectedUrl : CUSTOMERS' @submit.prevent="submitData" :class="{ 'walkin-mode': isWalkin, 'b2b-mode': isB2b }">
            <!-- ===== Branch selection gate (new patient, multiple authorised branches) ===== -->
            <div v-if="!selectedUrl && !branchConfirmed" class="text-center py-5">
                <h5 class="section-title mb-3">{{ $t('Select Branch') }}</h5>
                <p class="text-muted">{{ $t('Choose the branch for this patient, then confirm. This becomes the Original and Current branch.') }}</p>
                <div class="row justify-content-center">
                    <div class="col-12 col-md-6">
                        <select class="form-control" v-model="gateBranch">
                            <option value="">{{ $t('Select Branch') }}</option>
                            <option v-for="branch in branchList" :key="'gate' + branch.id" :value="branch.id">{{ branch.value }}</option>
                        </select>
                    </div>
                </div>
                <button type="button" class="btn btn-primary mt-3" :disabled="!gateBranch" @click="confirmBranch">
                    {{ $t('Confirm Branch') }}
                </button>
            </div>

            <div v-show="selectedUrl || branchConfirmed">
            <!-- ===== Section 1: Patient Type ===== -->
            <h5 class="section-title border-bottom mb-4 pb-2">{{ $t('Patient Type') }}</h5>
            <div class="row">
                <div class="col-12">
                    <div class="d-flex flex-wrap" style="gap: 1.5rem;">
                        <div class="form-check" v-for="opt in patientTypeList" :key="'pt' + opt.id">
                            <input class="form-check-input" type="radio" :id="'patient_type_' + opt.id"
                                name="patient_type" :value="opt.id" v-model="formData.customer_group_id"
                                :disabled="opt.disabled">
                            <label class="form-check-label" :class="{ 'text-muted': opt.disabled }"
                                :for="'patient_type_' + opt.id">{{ opt.value }}</label>
                        </div>
                    </div>
                    <small class="text-danger" v-if="$errorMessage(errors, 'customer_group_id')">{{
                        $errorMessage(errors, 'customer_group_id') }}</small>
                </div>
            </div>

            <!-- ===== Section 2: Personal Information ===== -->
            <h5 class="section-title border-bottom mb-4 pb-2">{{ $t('Personal Information') }}</h5>
            <div class="row">
                <div class="col-12 col-sm-6 col-md-2">
                    <app-form-group :class="'required always-required b2b-required'" type="select" :list="Prefix" list-value-field="name"
                        :label="$t('Prefix')" :required="true" :error-message="$errorMessage(errors, 'prefix')"
                        :placeholder="$placeholder('Prefix')" v-model="formData.prefix" />
                </div>
                <div class="col-12 col-sm-6 col-md-3">
                    <app-form-group :label="$t('first_name')" :class="'required always-required b2b-required'" v-model="formData.first_name"
                        :error-message="$errorMessage(errors, 'first_name')" :placeholder="$placeholder($t('first_name'))"
                        :required="true" />
                </div>
                <div class="col-12 col-sm-6 col-md-3">
                    <app-form-group :label="$t('last_name')" :class="'required b2b-required'" v-model="formData.last_name"
                        :error-message="$errorMessage(errors, 'last_name')" :placeholder="$placeholder($t('last_name'))"
                        :required="!isWalkin" />
                </div>
                <div class="col-12 col-sm-6 col-md-4">
                    <app-form-group :label="$t('Patient Name Hindi')" v-model="formData.patient_name_hindi"
                        :error-message="$errorMessage(errors, 'patient_name_hindi')"
                        :placeholder="$placeholder($t('Patient Name Hindi'))" />
                </div>
            </div>
            <div class="row">
                <div class="col-12 col-sm-6 col-md-4">
                    <label>{{ $t('Primary Email') }}</label>
                    <div class="row no-gutters">
                        <div class="col-10">
                            <app-input v-model="inputEmailRow[0].email" id="primary_email"
                                :error-message="$errorMessage(errors, 'email_details.0.email')"
                                :placeholder="$placeholder($t('email'))" />
                        </div>
                        <div class="col-2 pl-1">
                            <button class="btn default-base-color btn-block" type="button" @click="toggleSecondaryEmail">
                                <app-icon class="size-19 primary-text-color" :name="showSecondaryEmail ? 'trash' : 'plus'" />
                            </button>
                        </div>
                    </div>
                    <div v-if="showSecondaryEmail" class="mt-2">
                        <label>{{ $t('Secondary Email') }}</label>
                        <app-input v-model="inputEmailRow[1].email" id="secondary_email"
                            :error-message="$errorMessage(errors, 'email_details.1.email')"
                            :placeholder="$placeholder($t('email'))" />
                    </div>
                </div>
                <div :class="'col-12 col-sm-6 col-md-4 ' + (!isPartner ? 'required' : '')">
                    <label>{{ $t('Primary Phone') }}</label>
                    <div class="row no-gutters">
                        <div class="col-4 pr-1">
                            <select v-model="inputPhoneNumberRow[0].country_code" class="form-control px-1">
                                <option v-for="cc in phoneCountryCodes" :key="'p0' + cc.code" :value="cc.code">{{ cc.code }} {{ cc.name }}</option>
                            </select>
                        </div>
                        <div class="col-6">
                            <app-input v-model="inputPhoneNumberRow[0].phone_number" type="number" id="primary_phone"
                                :error-message="$errorMessage(errors, 'phone_number_details.0.phone_number')"
                                :placeholder="$placeholder($t('phone_number'))" />
                        </div>
                        <div class="col-2 pl-1">
                            <button class="btn default-base-color btn-block" type="button" @click="toggleSecondaryPhone">
                                <app-icon class="size-19 primary-text-color" :name="showSecondaryPhone ? 'trash' : 'plus'" />
                            </button>
                        </div>
                    </div>
                    <div v-if="showSecondaryPhone" class="mt-2">
                        <label>{{ $t('Secondary Phone') }}</label>
                        <div class="row no-gutters">
                            <div class="col-4 pr-1">
                                <select v-model="inputPhoneNumberRow[1].country_code" class="form-control px-1">
                                    <option v-for="cc in phoneCountryCodes" :key="'p1' + cc.code" :value="cc.code">{{ cc.code }} {{ cc.name }}</option>
                                </select>
                            </div>
                            <div class="col-8">
                                <app-input v-model="inputPhoneNumberRow[1].phone_number" type="number" id="secondary_phone"
                                    :error-message="$errorMessage(errors, 'phone_number_details.1.phone_number')"
                                    :placeholder="$placeholder($t('phone_number'))" />
                            </div>
                        </div>
                    </div>
                </div>
                <div class="col-12 col-sm-6 col-md-4">
                    <app-form-group type="date" v-model="formData.registration_date"
                        :min-date="regMinDate" :max-date="regMaxDate" :readOnly="!canBackdate"
                        :error-message="$errorMessage(errors, 'registration_date')" :label="$t('Registration Date')"
                        :placeholder="$placeholder($t('Registration Date'))" />
                </div>
            </div>
            <div class="row">
                <div class="col-12 col-sm-6 col-md-3">
                    <app-form-group :class="'required always-required b2b-required'" type="select" :label="$t('Gender')" :list="[
                        { id: '', name: $t('Select Gender') },
                        { id: 'male', name: $t('male') },
                        { id: 'female', name: $t('female') },
                        { id: 'other', name: $t('other') }
                    ]" list-value-field="name" :page="'modal'" v-model="formData.gender"
                        :error-message="$errorMessage(errors, 'gender')" :placeholder="$placeholder($t('Gender'))" />
                </div>
                <div class="col-12 col-sm-6 col-md-3" :class="{ required: dobRequired }">
                    <app-form-group type="date" :label="$t('Date of birth')"
                        :class="dobRequired ? 'required b2b-required' : 'b2b-required'"
                        v-model="formData.date_of_birth" :error-message="$errorMessage(errors, 'date_of_birth')"
                        :placeholder="'dd-mm-yyyy'" :required="dobRequired" />
                </div>
                <div class="col-12 col-sm-6 col-md-3" :class="{ required: dobRequired }">
                    <label>{{ $t('Age') }}</label>
                    <app-input :type="ageEditable ? 'number' : 'text'" v-model="formData.age" :readOnly="!ageEditable"
                        :error-message="$errorMessage(errors, 'age')"
                        :placeholder="ageEditable ? $t('Enter age') : 'Auto'" />
                </div>
                <div class="col-12 col-sm-6 col-md-3 required">
                    <app-form-group type="radio" radio-checkbox-name="legacy_patient" :label="$t('Legacy Patient')" :list="[
                        { id: 'No', value: $t('No') },
                        { id: 'Yes', value: $t('Yes') }
                    ]" v-model="formData.legacy_patient"
                        :error-message="$errorMessage(errors, 'legacy_patient')" />
                </div>
            </div>

            <!-- ===== Section 3: Branch Information ===== -->
            <h5 class="section-title border-bottom mb-4 pb-2">{{ $t('Branch Information') }}</h5>
            <div class="row">
                <div class="col-12 col-sm-6 col-md-4 required always-required">
                    <label>{{ $t('Original Branch') }}</label>
                    <div class="form-group">
                        <select id="original_branch" class="original_branch form-control"
                            name="original_branch" v-model="formData.original_branch" style="width: 100%;">
                            <option v-for="branch in branchList" :key="branch.id" :value="branch.id">
                                {{ branch.value }}
                            </option>
                        </select>
                        <small class="text-danger" v-if="$errorMessage(errors, 'original_branch')">{{ $errorMessage(errors, 'original_branch') }}</small>
                    </div>
                </div>
                <div class="col-12 col-sm-6 col-md-4 required">
                    <label>{{ $t('Current Branch') }}</label>
                    <div class="form-group">
                        <select id="current_branch" class="form-control"
                            name="current_branch" v-model="formData.current_branch" style="width: 100%;" :disabled="!canReassign">
                            <option value="">{{ $t('Select Branch') }}</option>
                            <option v-for="branch in branchList" :key="'cb' + branch.id" :value="branch.id">
                                {{ branch.value }}
                            </option>
                        </select>
                        <small class="text-danger" v-if="$errorMessage(errors, 'current_branch')">{{ $errorMessage(errors, 'current_branch') }}</small>
                    </div>
                </div>
                <div class="col-12 col-sm-6 col-md-4">
                    <app-form-group type="select" :list="ExaminationVenue" list-value-field="name" :page="'modal'"
                        :label="$t('Examination Venue')" :required="true"
                        :error-message="$errorMessage(errors, 'examination_venue')"
                        :placeholder="$placeholder('Examination Venue')" v-model="formData.examination_venue" />
                </div>
            </div>

            <!-- ===== Section 4: Clinical Information ===== -->
            <h5 class="section-title border-bottom mb-4 pb-2">{{ $t('Clinical Information') }}</h5>
            <div class="row">
                <div class="col-12 col-sm-6 col-md-3 required b2b-required">
                    <label>{{ $t('Primary complaint') }}</label>
                    <app-input type="search-and-select" :inputclearable="false"
                        :placeholder="$t('Select Primary complaint')" :options="primaryComplaintOptions"
                        v-model="formData.primary_complaint" :error-message="$errorMessage(errors, 'primary_complaint')" />
                </div>
                <div class="col-12 col-sm-6 col-md-3">
                    <app-form-group :class="'required b2b-required'" type="radio" radio-checkbox-name="diabetic" :label="$t('Diabetic')" :list="[
                        { id: 'No', value: $t('No') },
                        { id: 'Yes', value: $t('Yes') }
                    ]" v-model="formData.diabetic"
                        :error-message="$errorMessage(errors, 'diabetic')" />
                </div>
                <div class="col-12 col-sm-6 col-md-3 required b2b-required">
                    <label>{{ $t('Current Weight') }} ({{ weightUnit || 'kgs' }})</label>
                    <app-input type="number" v-model="formData.weight" :error-message="$errorMessage(errors, 'weight')"
                        :placeholder="$placeholder($t('Current Weight'))" onkeydown="if(event.key==='.'){event.preventDefault();}"
                        oninput="event.target.value = event.target.value.replace(/[^0-9]*/g,'');" />
                </div>
                <div class="col-12 col-sm-6 col-md-3">
                    <app-form-group type="select" :list="ActivityLevel" list-value-field="name" :page="'modal'"
                        v-model="formData.activity_level" :error-message="$errorMessage(errors, 'activity_level')"
                        :label="$t('Activity Level')" :placeholder="$placeholder($t('Activity Level'))" />
                </div>
            </div>

            <!-- ===== Section 5: Important Notes ===== -->
            <h5 class="section-title border-bottom mb-4 pb-2">{{ $t('Important Notes') }}</h5>
            <div class="row">
                <div class="col-12">
                    <app-form-group type="textarea" :text-area-rows="3" v-model="formData.important_notes"
                        :error-message="$errorMessage(errors, 'important_notes')"
                        :placeholder="$placeholder($t('Important Notes'))" />
                </div>
            </div>

            <!-- ===== Section 6: How Did You Find Us? ===== -->
            <h5 class="section-title border-bottom mb-2 pb-2">{{ $t('How Did You Find Us?') }}</h5>
            <p class="text-muted mb-3"><small>* {{ $t('Please fill at least one field below') }}</small></p>
            <div class="row">
                <div :class="'col-12 col-sm-6 col-md-4 b2b-required ' + ((!isPartner || isB2b) ? 'required' : '')">
                    <label>{{ $t('Referred by') }}</label>
                    <div class="form-group">
                        <select id="referred_by" class="referredby form-control"
                            name="referred_by" v-model="formData.referred_by" style="width: 100%;">
                            <option v-for="row in referredBy" :key="row.id" :value="row.id">{{ row.value }}</option>
                        </select>
                        <small class="text-danger" v-if="$errorMessage(errors, 'referred_by')">{{ $errorMessage(errors, 'referred_by') }}</small>
                    </div>
                </div>
                <div :class="getSourceColumnClass()">
                    <app-form-group :class="(!isPartner ? 'required' : '')" type="select" input-class=" form_source" :list="Source" list-value-field="name" :page="'modal'"
                        v-model="formData.source" :error-message="$errorMessage(errors, 'source')"
                        :label="$t('How Did You Hear About Us')" :placeholder="$placeholder($t('How Did You Hear About Us'))" />
                </div>
                <div :class="getCampColumnClass()">
                    <label>{{ $t('Camp Name') }}</label>
                    <div class="form-group">
                        <select id="camp_name" class="campname form-control"
                            name="camp_name" v-model="formData.camp_name" style="width: 100%;">
                            <option v-for="row in campList" :key="row.id" :value="row.id">{{ row.value }}</option>
                        </select>
                        <small class="text-danger" v-if="$errorMessage(errors, 'camp_name')">{{ $errorMessage(errors, 'camp_name') }}</small>
                    </div>
                </div>
            </div>

            <!-- ===== Section 7: Additional Information ===== -->
            <h5 class="section-title border-bottom mb-4 pb-2">{{ $t('Additional Information') }}</h5>
            <div class="row">
                <div class="col-12 col-sm-6 col-md-6">
                    <app-form-group type="select" :class="(!isPartner ? 'required' : '')" :list="PreferredLanguage" list-value-field="name"
                        :page="'modal'" v-model="formData.preferred_language"
                        :error-message="$errorMessage(errors, 'preferred_language')" :label="$t('Preferred language')"
                        :placeholder="$placeholder($t('Preferred language'))" />
                </div>
                <div class="col-12 col-sm-6 col-md-6">
                    <app-form-group v-model="formData.occupation" :error-message="$errorMessage(errors, 'occupation')"
                        :label="$t('Occupation')" :placeholder="$placeholder($t('Job Title'))" />
                </div>
            </div>
            <div class="row">
                <div class="col-12 col-sm-6 col-md-6">
                    <app-form-group :label="$t('Linked patients')" v-model="formData.linked_patients"
                        :error-message="$errorMessage(errors, 'linked_patients')"
                        :placeholder="$placeholder($t('Patient Names, comma-separated'))" />
                </div>
                <div class="col-12 col-sm-6 col-md-6">
                    <app-form-group :label="$t('Guardian Info')" v-model="formData.guardian_name_relationship"
                        maxlength="30" :error-message="$errorMessage(errors, 'guardian_name_relationship')"
                        :placeholder="$placeholder($t('Name & relationship'))" />
                </div>
            </div>
            <div class="row">
                <div class="col-12 col-sm-6 col-md-4">
                    <app-form-group v-model="formData.discount" :error-message="$errorMessage(errors, 'discount')"
                        :label="$t('Discount %')" :placeholder="$placeholder($t('Discount'))" />
                </div>
                <div class="col-12 col-sm-6 col-md-4">
                    <app-form-group :label="$t('Satisfaction status')" type="radio" radio-checkbox-name="satisfaction_status" :list="[
                        { id: 'N/A', value: $t('N/A') },
                        { id: 'Yes', value: $t('Yes') },
                        { id: 'No', value: $t('No') }
                    ]" v-model="formData.satisfaction_status"
                        :error-message="$errorMessage(errors, 'satisfaction_status')" />
                </div>
                <div class="col-12 col-sm-6 col-md-4">
                    <app-form-group :label="$t('Do Not Disturb')" type="radio" radio-checkbox-name="dnd" :list="[
                        { id: 0, value: $t('No') },
                        { id: 1, value: $t('Yes') }
                    ]" v-model="formData.dnd" :error-message="$errorMessage(errors, 'dnd')" />
                </div>
            </div>
            <!-- ===== Section 8: Practitioner Information ===== -->
            <h5 class="section-title border-bottom mb-4 pb-2">{{ $t('Practitioner Information') }}</h5>
            <div class="row">
                <div class="col-12 col-sm-6 col-md-4 required">
                    <app-form-group v-model="formData.record_created_by"
                        :error-message="$errorMessage(errors, 'record_created_by')" :label="$t('Record Created By')"
                        :placeholder="$placeholder($t('Record Created By'))" disabled />
                </div>
                <div class="form-element col-12 col-md-4 mb-4 required">
                    <label>{{ $t('First Assigned To') }}</label>
                    <div class="form-group">
                        <select id="first_assigned_to" class="first_assigned_to form-control"
                            name="first_assigned_to" v-model="formData.first_assigned_to" style="width: 100%;" :disabled="!canReassign">
                            <option v-for="user in userList" :key="user.id" :value="user.id">{{ user.value }}</option>
                        </select>
                    </div>
                </div>
                <div class="form-element col-12 col-md-4 mb-4 required">
                    <label>{{ $t('Currently Assigned To') }}</label>
                    <div class="form-group">
                        <select id="currently_assigned_to" class="currently_assigned_to form-control"
                            name="currently_assigned_to" v-model="formData.currently_assigned_to" style="width: 100%;" :disabled="!canReassign">
                            <option v-for="user in userList" :key="user.id" :value="user.id">{{ user.value }}</option>
                        </select>
                    </div>
                </div>
            </div>

            <!-- ===== Section 9: Addresses ===== -->
            <h5 class="section-title border-bottom mb-4 pb-2">{{ $t('Addresses') }}</h5>
            <div v-for="(rowData, index) in inputAddressRow" class="mb-4 border rounded p-3" :key="'addr' + index">
                <div class="d-flex justify-content-between align-items-center mb-2">
                    <strong>{{ $t('Address') }} {{ index + 1 }}</strong>
                    <button v-if="index > 0" class="btn btn-sm btn-outline-danger" type="button" @click="removeAddressRow(index)">
                        <app-icon class="size-16" name="trash" />
                    </button>
                </div>
                <div class="row mb-3">
                    <div class="col-12">
                        <app-form-group :label="$t('Address Title')" type="radio" :radio-checkbox-name="'address_title_' + index" :list="[
                            { id: 'na', name: 'address', value: $t('NA'), disabled: addressHasData(index) },
                            { id: 'home', name: 'address', value: $t('home') },
                            { id: 'work', name: 'address', value: $t('work') },
                            { id: 'other', name: 'address', value: $t('Other') }
                        ]" v-model="inputAddressRow[index].name" :id="'title' + index"
                            :error-message="$errorMessage(errors, 'address_information_details.' + index + '.name')" />
                    </div>
                </div>
                <div class="row mb-3">
                    <div class="col-md-12">
                        <app-form-group :class="inputAddressRow[index].name !== 'na' ? 'required' : ''" :label="$t('Line 1')" v-model="inputAddressRow[index].line_1" :disabled="inputAddressRow[index].name === 'na'" :id="'line1' + index"
                            :error-message="$errorMessage(errors, 'address_information_details.' + index + '.line_1')"
                            :placeholder="$placeholder($t('Line 1'))" />
                    </div>
                    <div class="col-md-12">
                        <app-form-group :label="$t('Line 2')" v-model="inputAddressRow[index].line_2" :disabled="inputAddressRow[index].name === 'na'" :id="'line2' + index"
                            :placeholder="$placeholder($t('Line 2'))" />
                    </div>
                    <div class="col-md-12">
                        <app-form-group :label="$t('Line 3')" v-model="inputAddressRow[index].line_3" :disabled="inputAddressRow[index].name === 'na'" :id="'line3' + index"
                            :placeholder="$placeholder($t('Line 3'))" />
                    </div>
                </div>
                <div class="row mb-3">
                    <div class="col-md-6">
                        <label>{{ $t('country') }}</label>
                        <app-input class="margin-right-8" type="select" :list="countryList" :key="countryList.length"
                            list-value-field="name" v-model="inputAddressRow[index].country_id" :disabled="inputAddressRow[index].name === 'na'"
                            :error-message="$errorMessage(errors, 'country')" />
                    </div>
                    <div class="col-md-6">
                        <label>{{ $t('State') }}</label>
                        <app-input type="select" :list="statesList" :key="statesList.length" list-value-field="value"
                            v-model="inputAddressRow[index].area" :disabled="inputAddressRow[index].name === 'na'" :id="'area' + index"
                            :error-message="$errorMessage(errors, 'state')" :placeholder="$placeholder($t('area'))" />
                    </div>
                </div>
                <div class="row">
                    <div class="col-md-6" :class="inputAddressRow[index].name !== 'na' ? 'required' : ''">
                        <label>{{ $t('City') }}</label>
                        <app-input v-model="inputAddressRow[index].city" type="text" :disabled="inputAddressRow[index].name === 'na'" :id="'city' + index"
                            :error-message="$errorMessage(errors, 'address_information_details.' + index + '.city')" :placeholder="$placeholder($t('city'))" />
                    </div>
                    <div class="col-md-6" :class="inputAddressRow[index].name !== 'na' ? 'required' : ''">
                        <label>{{ $t('Pin Code') }}</label>
                        <app-input v-model="inputAddressRow[index].zip_code" type="number" :disabled="inputAddressRow[index].name === 'na'" :id="'zip_code' + index"
                            :error-message="$errorMessage(errors, 'address_information_details.' + index + '.zip_code')" :placeholder="$placeholder($t('Pin Code'))" />
                    </div>
                </div>
            </div>
            <div class="mb-3">
                <button v-if="inputAddressRow.length < 2" class="btn btn-sm btn-outline-primary" type="button" @click="appendAddressRow">
                    <app-icon class="size-16" name="plus" /> {{ $t('Add Address') }}
                </button>
            </div>
            </div>
        </form>
    </modal>
</template>
<script>
import FormHelperMixins from "../../../../common/Mixin/Global/FormHelperMixins";
import ModalMixin from "../../../../common/Mixin/Global/ModalMixin";
import { LINKED_CUSTOMERS, WEIGHT_UNIT, GET_CUSTOMERS_NAME } from "../../../Config/ApiUrl-CP";
import { CUSTOMERS, SELECTABLE_CUSTOMER_GROUP, ATTACHMENTS, BRANCH_OR_WAREHOUSE } from "../../../Config/ApiUrl-CPB";
import { COUNTRIES, SELECTABLE_STATES } from "../../../Config/ApiUrl-CP";
import CoreLibrary from "../../../../core/helpers/CoreLibrary";
import { numberToString } from "../../../../common/Helper/Support/TextHelper";
import { axiosGet, axiosPost, urlGenerator } from "../../../../common/Helper/AxiosHelper";
import { mapGetters } from 'vuex';

export default {
    name: 'CustomerCreateEditModal',
    extends: CoreLibrary,
    mixins: [FormHelperMixins, ModalMixin],
    data() {
        return {
            selectedLinkedPatientsNames: [],
            weightUnit: '',
            attachments: [],
            userList: [],
            branchList: [],
            branchType: '',
            referredBy: [],
            campList: [],
            attachmentDescriptions: {},
            numberToString,
            customerGroupInputKey: 0,
            customerGroupOptions: {
                url: urlGenerator("app/selectable-customer-groups"),
                query_name: "search_by_name",
                per_page: 10,
                loader: "app-pre-loader", // by default 'app-overlay-loader'
                modifire: ({ id, name: value }) => ({ id, value }),
                prefetch: false,
            },
            formData: {
                customer_group_id: 1,
                addresses: {},
                contacts: {},
                tin: '',
                tenant_id: '',
                gender: '',
                satisfaction_status: 'N/A',
                title: '',
                original_branch: '',
                referred_by: '',
                camp_name: '',
                record_created_by: window.user.full_name || '',
                registration_date: '', // Added registration_date property
                addToReport: false,
                first_assigned_to: window.user.id || '',
                currently_assigned_to: window.user.id || '',
                legacy_patient: 'No',
                dnd: 0,
                current_branch: '',
                diabetic: '',
            },
            branchOrWareHoueeOptions: {
                url: urlGenerator("app/selectable/branches"),
                queryName: 'search_by_name',
                per_page: 10,
                loader: "app-pre-loader", // by default 'app-overlay-loader'
                modifire: (value) => ({ id: value.id, value: value.name, type: value.type }),
            },
            Prefix: [
                { id: '', name: this.$t('Select Prefix') },
                { id: 'Mr.', name: this.$t('Mr.') },
                { id: 'Ms.', name: this.$t('Ms.') },
                { id: 'Mrs.', name: this.$t('Mrs.') },
                { id: 'Dr.', name: this.$t('Dr.') },
            ],
            CurrentUser: [
                { id: '', name: this.$t('Select User') },
                { id: window.user.full_name || '', name: this.$t(window.user.full_name || '') },
            ],
            GSTTypeList: [
                { id: '', name: this.$t('select_type') },
                { id: 'Unregistered', name: this.$t('Unregistered') },
                { id: 'Registered', name: this.$t('Registered') },
                { id: 'Composite', name: this.$t('Composite') },
            ],
            ExaminationVenue: [
                { id: '', name: this.$t('Select Examination Venue') },
                { id: 'Branch', name: this.$t('Branch') },
                { id: 'Home Visit', name: this.$t('Home Visit') },
                { id: 'Doctors Clinic', name: this.$t('Doctors Clinic') },
                { id: 'Online', name: this.$t('Online') },
                { id: 'Camp', name: this.$t('Camp') },
            ],
            ActivityLevel: [
                { id: '', name: this.$t('Select Activity Level') },
                { id: 'Home Based', name: this.$t('Home Based') },
                { id: 'Sedentary', name: this.$t('Sedentary') },
                { id: 'Morning Walks', name: this.$t('Morning Walks') },
                { id: 'On foot Occupation', name: this.$t('On foot Occupation') },
                { id: 'Recreational sport', name: this.$t('Recreational sport') },
                { id: 'Professional sport', name: this.$t('Professional sport') },
            ],
            LegacyPatient: [
                { id: '', name: this.$t('Select Legacy Patient') },
                { id: 'Yes', name: this.$t('Yes') },
                { id: 'No', name: this.$t('No') },
            ],
            PreferredLanguage: [
                { id: '', name: this.$t('Select Preferred Language') },
                { id: 'English', name: this.$t('English') },
                { id: 'Hindi', name: this.$t('Hindi') },
                { id: 'Marathi', name: this.$t('Marathi') },
            ],
            Source: [
                { id: '', name: this.$t('Select Source') },
                { id: 'Camp', name: this.$t('Camp') },
                { id: 'Google Business Page', name: this.$t('Google Business Page') },
                { id: 'Google Ad', name: this.$t('Google Ad') },
                { id: 'Website', name: this.$t('Website') },
                { id: 'Doctor', name: this.$t('Doctor') },
                { id: 'Walk-in', name: this.$t('Walk-in') },
                { id: 'YouTube', name: this.$t('YouTube') },
                { id: 'Facebook', name: this.$t('Facebook') },
                { id: 'Instagram', name: this.$t('Instagram') },
                { id: 'Referred by friend', name: this.$t('Referred by friend') },
                { id: 'Other', name: this.$t('Other') },
            ],
            PrimaryComplaint: [
                { id: '', name: this.$t('Select Primary Complaint') },
                { id: 'Pes planus', name: this.$t('Pes planus') },
                { id: 'Pes cavus', name: this.$t('Pes cavus') },
                { id: 'Over pronation', name: this.$t('Over pronation') },
                { id: 'Over supination', name: this.$t('Over supination') },
                { id: 'Diabetic Foot Syndrome', name: this.$t('Diabetic Foot Syndrome') },
                { id: 'Corn or Callus', name: this.$t('Corn or Callus') },
                { id: 'Deformed foot', name: this.$t('Deformed foot') },
                { id: 'Knee pain', name: this.$t('Knee pain') },
                { id: 'Knocked knee', name: this.$t('Knocked knee') },
                { id: 'Bowed legs', name: this.$t('Bowed legs') },
                { id: 'Hallux valugs', name: this.$t('Hallux valugs') },
                { id: 'Low back pain', name: this.$t('Low back pain') },
                { id: 'Plantar fasciitis', name: this.$t('Plantar fasciitis') },
                { id: 'Heel spur', name: this.$t('Heel spur') },
                { id: 'Achilles tendonitis', name: this.$t('Achilles tendonitis') },
                { id: 'Sesamoiditis', name: this.$t('Sesamoiditis') },
                { id: 'Toe pain', name: this.$t('Toe pain') },
                { id: 'Metatarsalgia', name: this.$t('Metatarsalgia') },
                { id: 'Mortons neuroma', name: this.$t('Mortons neuroma') },
                { id: 'Charcot foot', name: this.$t('Charcot foot') },
                { id: 'Diabetic wound', name: this.$t('Diabetic wound') },
                { id: 'Edema', name: this.$t('Edema') },
                { id: 'LLD', name: this.$t('LLD') },
                { id: 'Navicular drop or drift', name: this.$t('Navicular drop or drift') },
            ],
            linkedPatientsList: [],
            maxSelections: 3,
            userOptions: {
                url: urlGenerator("app/selectable/users"),
                queryName: 'search_by_name',
                per_page: 50,
                loader: "app-pre-loader", // by default 'app-overlay-loader'
                modifire: (value) => ({ id: value.id, value: value.full_name }),
            },
            CUSTOMERS,
            SELECTABLE_CUSTOMER_GROUP,
            COUNTRIES,
            typeList: [
                { id: '', name: this.$t('select_type') },
                { id: 'home', name: this.$t('home') },
                { id: 'work', name: this.$t('work') },
                { id: 'office', name: this.$t('office') }
            ],
            countryList: [],
            statesList: [],
            inputAddressRow: [],
            index: 0,
            inputEmailRow: [{}],
            removableEmailRow: true,
            inputPhoneNumberRow: [{ country_code: '+91' }],
            removablePhoneNumberRow: true,
            showSecondaryEmail: false,
            showSecondaryPhone: false,
            inputAddressRow: [
                {
                    name: "na",
                    city: "",
                    country_id: "",
                    zip_code: "",
                    area: "",
                    details: "",
                    line_1: "",
                    line_2: "",
                    line_3: "",
                    address_type: "",
                }
            ],
            removableAddressRow: true,
            isPartner: false,
            branchConfirmed: false,
            branchesLoaded: false,
            gateBranch: '',
            phoneCountryCodes: [
                { code: '+91', name: 'India' },
                { code: '+1', name: 'USA/Canada' },
                { code: '+44', name: 'UK' },
                { code: '+61', name: 'Australia' },
                { code: '+971', name: 'UAE' },
                { code: '+65', name: 'Singapore' },
                { code: '+966', name: 'Saudi Arabia' },
                { code: '+60', name: 'Malaysia' },
                { code: '+49', name: 'Germany' },
                { code: '+33', name: 'France' },
                { code: '+81', name: 'Japan' },
                { code: '+86', name: 'China' },
                { code: '+880', name: 'Bangladesh' },
                { code: '+94', name: 'Sri Lanka' },
                { code: '+977', name: 'Nepal' },
                { code: '+92', name: 'Pakistan' },
                { code: '+27', name: 'South Africa' },
                { code: '+64', name: 'New Zealand' },
            ],
        }
    },
    created() {
        if (this.selectedUrl) this.branchConfirmed = true;
        this.getCountries();
        this.statesOptions();
        // if (!this.selectedUrl) this.setDefaultGroupId();

        this.loadWeightUnit();
        // Branch-dependent dropdowns are now loaded by the 'formData.original_branch'
        // watcher, which reliably fires whether the branch id is preset (edit) or
        // arrives asynchronously from the store (add).
        this.userListOptions();
        this.branchListOptions();
    },
    watch: {
        showModal(val) {
            if (val && !this.selectedUrl) {
                this.branchConfirmed = false;
                this.gateBranch = this.getBranchOrWarehouseId || '';
                this.maybeSkipBranchGate();
            }
        },
        errors(val) {
            if (val && Object.keys(val).length) {
                this.$nextTick(() => this.scrollToFirstError());
            }
        },
        inputEmailRow() {
            this.removableEmailRow = this.inputEmailRow.length <= 1;
        },
        inputPhoneNumberRow() {
            this.removablePhoneNumberRow = this.inputPhoneNumberRow.length <= 1;
        },

        'formData.date_of_birth'(newDateOfBirth) {
            if (newDateOfBirth) {
                const today = new Date();
                const birthDate = new Date(newDateOfBirth);
                const age = today.getFullYear() - birthDate.getFullYear();

                // Adjust age based on month and day of birth
                if (today.getMonth() < birthDate.getMonth() || (today.getMonth() === birthDate.getMonth() && today.getDate() < birthDate.getDate())) {
                    this.formData.age = age - 1; // Update the age property directly
                } else {
                    this.formData.age = age; // Update the age property directly
                }
            }
        },

        // 'getUserId': {
        //     immediate: true,
        //     handler(newId) {
        //         if (!newId) return;
        //         this.formData.first_assigned_to = newId;
        //         this.formData.currently_assigned_to = newId;
        //         this.ssKey++;
        //         console.log(this.formData.currently_assigned_to);
        //     }
        // },

        'getBranchOrWarehouseId': {
            immediate: true,
            handler(newId) {
                if (!newId) return;
                this.formData.original_branch = newId;
                this.ssKey++;
            }
        },
        // Single source of truth for branch-dependent dropdowns (incl. 'Referred by').
        // The branch id can arrive from the store AFTER created(), so reacting to the
        // value itself guarantees these lists always load. Fixes the intermittent
        // empty 'Referred by' list that previously required a page refresh.
        'formData.original_branch': {
            immediate: true,
            handler(newId, oldId) {
                if (!newId || newId === oldId) return;
                // Current Branch defaults to the Original Branch on first selection.
                if (!this.formData.current_branch) this.formData.current_branch = newId;
                this.fetchlinkedCustomers();
                this.fetchLinkedCamps();
                this.getBranchData();
            }
        },
        'getBranchOrWarehouseInfo': {
            immediate: true,
            handler(val) {
                const branchType = (val && val.branch_type ? val.branch_type : '').toString().toLowerCase().trim();
                this.isPartner = (branchType === 'partner');
                // Only a B2B branch defaults NEW patients to the B2B patient type.
                // Partner branches no longer default to B2B (B2B is disabled there).
                if (branchType === 'b2b' && !this.formData.id) {
                    this.formData.customer_group_id = 3;
                }
            }
        },
        // getBranchOrWarehouseInfo(val) {
        //     console.log(val);
        //     immediate: true,
        //     if(val != undefined && val.branch_type == 'partner') {
        //         this.isPartner = true;
        //     } else {
        //         this.isPartner = false;
        //     }
        // }
    },
    mounted() {
        this.formData.tenant_id = this.getTenantId;
        this.setInitialRegistrationDate(); // Set initial value of registration_date

        // Initialize Select2
        let self = this;
        $('.first_assigned_to').select2({ placeholder: 'Select a user',dropdownParent: $("#customer-modal") }).on("change", function () {
            self.onSelectChangeFirstAssignedTo(this.value);
        });

        $('.original_branch').select2({ placeholder: 'Branch of first visit',dropdownParent: $("#customer-modal") }).on("change", function () {
            self.onSelectChangeOriginalBranch(this.value);
        }); 
        $('.referredby').select2({ placeholder: 'Select Referred by', dropdownParent: $("#customer-modal") }).on("change", function () {
            self.onSelectChangeReferredBy(this.value);
        }); 

        $('.currently_assigned_to').select2({ placeholder: 'Select a user', }).on("change", function () {
            self.onSelectChange(this.value);
        });
        $('.campname').select2({ placeholder: 'Select Camp name', dropdownParent: $("#customer-modal") }).on("change", function () {
            self.onSelectChangeCampName(this.value);
        });
    },
    computed: {
        ...mapGetters(["getTenantId"]),
        ...mapGetters(['getBranchOrWarehouseName']),
        ...mapGetters(['getBranchOrWarehouseId']),
        ...mapGetters(['getUserId']),
        ...mapGetters(['getBranchOrWarehouseInfo']),

        isWalkin() {
            return this.formData.customer_group_id == 2;
        },
        isB2b() {
            return this.formData.customer_group_id == 3;
        },
        // Patient-type options with per-option disabling driven by branch type:
        //  - B2B branch     -> only B2B selectable (Regular/Walk-in greyed out)
        //  - Podiapro/Partner -> B2B greyed out (Regular/Walk-in selectable)
        patientTypeList() {
            const isB2bBranch = (this.branchType || '').toLowerCase() === 'b2b';
            return [
                { id: 1, value: this.$t('Regular Patient'), disabled: isB2bBranch },
                { id: 2, value: this.$t('Walk-In Customer'), disabled: isB2bBranch },
                { id: 3, value: this.$t('B2B'), disabled: !isB2bBranch },
            ];
        },
        isPartnerBranch() {
            return (this.branchType || '').toLowerCase() === 'partner';
        },
        // DOB / Age are NOT mandatory for Walk-in, Partner-branch, or B2B patients.
        dobRequired() {
            return !this.isWalkin && !this.isPartnerBranch && !this.isB2b;
        },
        // Age is manually entered (editable numeric) for Partner and B2B patients;
        // otherwise it's auto-calculated (read-only) from the date of birth.
        ageEditable() {
            return this.isPartnerBranch || this.isB2b;
        },
        canReassign() {
            const roles = (window.user && window.user.roles) || [];
            return roles.some(r => r.id == 1 || ['Super Admin', 'App Admin', 'Manager'].includes(r.name));
        },
        canBackdate() {
            const roles = (window.user && window.user.roles) || [];
            return roles.some(r => r.id == 1 || ['Super Admin', 'App Admin'].includes(r.name));
        },
        // Registration date may only be today or up to 2 days back.
        regMaxDate() {
            return new Date();
        },
        regMinDate() {
            const d = new Date();
            d.setDate(d.getDate() - 2);
            return d;
        },

        primaryComplaintOptions() {
            return {
                url: urlGenerator("app/selectable-pathologies"),
                // queryName: 'search_by_name',
                query_name: "search_by_name",
                per_page: 50,
                // params: { 'branch_id': this.getBranchOrWarehouseId, },
                loader: "app-pre-loader", // by default 'app-overlay-loader'
                modifire: (value) => ({ id: value.id, value: value.chief_complaint_name }),
            }
        },

        doctorsOptions() {
            if (this.formData.original_branch == '') {
                // Return an empty object or null if original_branch is null
                return {};
            }

            // If original_branch is available, construct and return the API call object
            return {
                url: urlGenerator("app/selectable/doctors"),
                queryName: 'search_by_name',
                per_page: 50,
                params: { 'branch_id': this.formData.original_branch },
                loader: "app-pre-loader", // by default 'app-overlay-loader'
                modifire: (value) => ({ id: value.id, value: value.full_name }),
            }
        },

        campsOptions() {
            if (this.formData.original_branch === null) {
                // Return an empty object or null if original_branch is null
                return null;
            }

            // If original_branch is available, construct and return the API call object
            return {
                url: urlGenerator("app/selectable/camps"),
                queryName: 'search_by_name',
                per_page: 50,
                params: { 'branch_id': this.formData.original_branch },
                loader: "app-pre-loader", // by default 'app-overlay-loader'
                modifire: (value) => ({ id: value.id, value: value.camp_name }),
            }
        },
    },
    methods: {
        onSelectChangeFirstAssignedTo(val) {
            this.formData.first_assigned_to = val;
        },
        onSelectChangeOriginalBranch(val) {
            // Setting original_branch triggers the 'formData.original_branch' watcher,
            // which loads the branch-dependent dropdowns (doctors/'Referred by',
            // camps, linked customers, branch data).
            this.formData.original_branch = val;
            // this.formData.source = null;
            //`${BRANCH_OR_WAREHOUSE}/${row.id}`
        },
        onSelectChange(val) {
            this.formData.currently_assigned_to = val;
        },
        onSelectChangeCampName(val) {
            this.formData.camp_name = val;
        },
        onSelectChangeReferredBy(val) {
            this.formData.referred_by = val;
        },
        toggleSecondaryEmail() {
            if (!this.showSecondaryEmail) {
                if (this.inputEmailRow.length < 2) this.inputEmailRow.push({});
                this.showSecondaryEmail = true;
            } else {
                if (this.inputEmailRow.length > 1) this.inputEmailRow.splice(1, 1);
                this.showSecondaryEmail = false;
            }
        },
        toggleSecondaryPhone() {
            if (!this.showSecondaryPhone) {
                if (this.inputPhoneNumberRow.length < 2) this.inputPhoneNumberRow.push({ country_code: '+91' });
                this.showSecondaryPhone = true;
            } else {
                if (this.inputPhoneNumberRow.length > 1) this.inputPhoneNumberRow.splice(1, 1);
                this.showSecondaryPhone = false;
            }
        },
        userListOptions() {
            axiosGet(`app/selectable/users`).then(response => {
                for (const item of response.data.data) {
                    this.userList.push({
                        id: item.id,
                        value: item.full_name,
                    });
                }
            }).catch(error => {
                console.error(error);
            });
        },
        fetchLinkedDoctors() {
            const savedReferredBy = this.formData.referred_by;
            axiosGet(`app/selectable/doctors?branch_id=${this.formData.original_branch}`).then(response => {
                const items = (response.data && response.data.data) ? response.data.data : (response.data || []);
                const seen = new Set();
                const list = [];
                for (const item of items) {
                    if (seen.has(item.id)) continue;
                    seen.add(item.id);
                    list.push({ id: item.id, value: item.full_name });
                }
                this.referredBy = list;
                this.reinitReferredBySelect2(savedReferredBy);
            }).catch(error => {
                console.error(error);
            });
        },
        // select2 is initialised (empty) at mount and won't recognise <option>s Vue
        // adds later — leaving them stale double-renders entries in the dropdown.
        // Rebuild the widget from the current options and restore the saved value.
        reinitReferredBySelect2(savedReferredBy) {
            this.formData.referred_by = savedReferredBy;
            this.$nextTick(() => {
                const self = this;
                const $sel = $('.referredby');
                try { if ($sel.hasClass('select2-hidden-accessible')) $sel.select2('destroy'); } catch (e) { /* noop */ }
                $sel.val(savedReferredBy);
                $sel.select2({ placeholder: 'Select Referred by', dropdownParent: $("#customer-modal") });
                $sel.off('change').on('change', function () { self.onSelectChangeReferredBy(this.value); });
            });
        },
        fetchLinkedCamps() {
            this.campList = [];
            axiosGet(`app/selectable/camps?branch_id=${this.formData.original_branch}`).then(response => {
                for (const item of response.data.data) {
                    this.campList.push({
                        id: item.id,
                        value: item.camp_name,
                    });
                }
            }).catch(error => {
                console.error(error);
            });
        },
        branchListOptions() {
            axiosGet(`app/selectable/branches`).then(response => {
                for (const item of response.data.data) {
                    this.branchList.push({
                        id: item.id,
                        value: item.name,
                    });
                }
                this.branchesLoaded = true;
                this.maybeSkipBranchGate();
            }).catch(error => {
                console.error(error);
            });
        },
        maybeSkipBranchGate() {
            if (this.selectedUrl || this.branchConfirmed || !this.branchesLoaded) return;
            if (this.branchList.length === 1) {
                this.gateBranch = this.branchList[0].id;
                this.confirmBranch();
            } else if (this.branchList.length === 0) {
                this.branchConfirmed = true;
            } else if (!this.gateBranch) {
                this.gateBranch = this.getBranchOrWarehouseId || '';
            }
        },
        confirmBranch() {
            if (!this.gateBranch) return;
            this.formData.original_branch = this.gateBranch;
            this.formData.current_branch = this.gateBranch;
            this.branchConfirmed = true;
            this.$nextTick(() => {
                $('.original_branch').val(this.gateBranch).trigger('change');
            });
        },
        scrollToFirstError() {
            setTimeout(() => {
                const modal = document.getElementById('customer-modal');
                if (!modal) return;
                const candidates = modal.querySelectorAll('.text-danger, .validation-error, .invalid-feedback');
                let target = null;
                for (const el of candidates) {
                    if (el.offsetParent !== null && el.textContent.trim()) { target = el; break; }
                }
                if (target) {
                    target.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }
            }, 100);
        },

        getBranchData() {
            let branchDatanew = []; //`${BRANCH_OR_WAREHOUSE}/${row.id}`
            axiosGet(`${BRANCH_OR_WAREHOUSE}/${this.formData.original_branch}`).then(response => {
                branchDatanew = response.data;
                // ✅ If state ID exists, auto-select it in dropdown
                if (branchDatanew.stateid && branchDatanew.stateid !== "") {
                    this.inputAddressRow[this.index].area = branchDatanew.stateid;
                }
                // Track the selected branch's type (case-insensitive; stored value may be 'B2B').
                this.branchType = (branchDatanew.branch_type || '').toString().toLowerCase().trim();
                const isB2bBranch = this.branchType === 'b2b';
                // Patient type follows the branch: B2B branch forces B2B (3);
                // Podiapro/Partner branches disallow B2B, so reset a B2B selection.
                if (isB2bBranch) {
                    this.formData.customer_group_id = 3;
                } else if (this.formData.customer_group_id == 3) {
                    this.formData.customer_group_id = 1;
                }
                // 'Referred by' source depends on the branch type: a b2b branch lists the
                // B2B Partners mapped to it; any other branch lists the doctors.
                if (isB2bBranch) {
                    this.fetchLinkedB2bPartners();
                } else {
                    this.fetchLinkedDoctors();
                }
            }).catch(error => {
                console.error(error);
                this.fetchLinkedDoctors();
            });
        },
        fetchLinkedB2bPartners() {
            const savedReferredBy = this.formData.referred_by;
            axiosGet(`app/selectable/b2b-partners?branch_id=${this.formData.original_branch}`).then(response => {
                const items = (response.data && response.data.data) ? response.data.data : response.data;
                const seen = new Set();
                const list = [];
                for (const item of (items || [])) {
                    if (seen.has(item.id)) continue;
                    seen.add(item.id);
                    list.push({ id: item.id, value: item.name });
                }
                this.referredBy = list;
                this.reinitReferredBySelect2(savedReferredBy);
            }).catch(error => {
                console.error(error);
            });
        },
        onGenderChange(selectedGender) {
            // Access the selected gender
            this.formData.gender = selectedGender;

            // Perform any action based on the selected gender
            console.log(`Selected gender: ${selectedGender}`);
        },
        handleLinkedPatientsChange() {
            console.log(this.formData.linked_patients.length);
            if (this.formData.linked_patients.length > this.maxSelections) {
                // If the user exceeds the maximum allowed selections, remove the latest added patients
                const removedPatients = this.formData.linked_patients.splice(this.maxSelections);
                // Show an error message to inform the user about the limit
                alert(`You can select up to ${this.maxSelections} linked patients.`);
            }
        },
        getSourceColumnClass() {
            return this.formData.source === 'Camp' ? 'col-12 col-sm-6 col-md-4' : 'col-12 col-sm-6 col-md-4';
        },
        getCampColumnClass() {
            return this.formData.source === 'Camp' ? 'col-12 col-sm-6 col-md-4' : 'd-none'; // Hide if source is not 'Camp'
        },
        loadWeightUnit() {
            const branchId = this.getBranchOrWarehouseId;
            // console.log(branchId);
            axiosGet(`${WEIGHT_UNIT}/${branchId}`).then(response => {
                // console.log('response:', response.data.weight_unit);
                const weightUnit = response.data.weight_unit;

                // Set the weightUnit value in the data
                this.weightUnit = weightUnit;
            }).catch(error => {
                console.error(error);
            });
        },
        fetchlinkedCustomers() {
            const branchId = this.formData.original_branch;
            this.linkedPatientsList = [];
            axiosGet(`${LINKED_CUSTOMERS}/${branchId}`).then(response => {
                console.log('response:', response.data.data);

                for (const item of response.data.data) {
                    this.linkedPatientsList.push({
                        id: item.id.toString(),
                        value: item.full_name,
                    });
                }
            }).catch(error => {
                console.error(error);
            });
        },
        getCurrentDate() {
            const today = new Date();
            const year = today.getFullYear();
            let month = today.getMonth() + 1; // Note: month is zero-based
            let day = today.getDate();

            // Pad month and day with leading zeroes if necessary
            if (month < 10) {
                month = '0' + month;
            }
            if (day < 10) {
                day = '0' + day;
            }

            return `${year}-${month}-${day}`;
        },

        setInitialRegistrationDate() {
            this.formData.registration_date = this.getCurrentDate(); // Set initial value of registration_date
        },
        getCountries() {
            axiosGet(COUNTRIES)
                .then(data => {
                    this.countryList = [{ name: this.$t('select_country') }, ...data.data];

                    // Find the ID of the country "India" in the countryList
                    const indiaCountry = this.countryList.find(country => country.name === 'India');
                    if (indiaCountry) {
                        // Set the country_id for India
                        this.inputAddressRow[this.index].country_id = indiaCountry.id;
                    } else {
                        console.log('India not found in country list.');
                    }
                })
                .catch(error => {
                    console.error(error);
                });
        },

        statesOptions() {
            this.statesList.unshift({ id: '', value: 'Select State' });
            try {
                axiosGet(`${SELECTABLE_STATES}`).then(response => {
                    if(this.formData.stateid == '') this.formData.stateid = response.data.data[0].id;
                    for (let item of response.data.data) {
                        this.statesList.push({
                            id: item.id,
                            value: item.state,
                        });
                    }
                });
            } catch (error) {
                console.error(error);
            }
        },
        submitData() {
            this.fieldStatus.isSubmit = true;
            this.loading = true;
            this.message = '';
            this.errors = {};

            this.formData.first_assigned_to = $($(".first_assigned_to")[0]).val();
            this.formData.currently_assigned_to = $($(".currently_assigned_to")[0]).val();
            this.formData.original_branch = $($(".original_branch")[0]).val();
            this.formData.referred_by = $($(".referredby")[0]).val();
            this.formData.camp_name = $($(".campname")[0]).val();



            this.formData.email_details = this.inputEmailRow.map((item) => ({
                'email': item.email,
                'email_type': item.email_type,
            })
            );
            this.formData.email = this.formData.email_details[0].email;

            this.formData.phone_number_details = this.inputPhoneNumberRow.map((item) => ({
                'phone_number': item.phone_number,
                'phone_number_type': item.country_code || '+91',
            })
            );

            const existingAddresses = this.formData.addresses.length ? this.formData.addresses.filter(address => address.id !== this.inputAddressRow[0].id) : [];

            this.formData.address_information_details =
                [...existingAddresses, ...this.inputAddressRow].map((item) => ({
                    'name': item.name,
                    'country_id': item.country_id,
                    'city': item.city,
                    'zip_code': item.zip_code,
                    'area': item.area,
                    'line_1': item.line_1,
                    'line_2': item.line_2,
                    'line_3': item.line_3,
                    'address_type': item.address_type,
                    'details': [item.line_1, item.line_2, item.line_3].filter(Boolean).join(', ') || item.details || '',
                })
                );

            this.formData.branch_id = this.getBranchOrWarehouseId;
            // console.log(this.formData.branch_id);

            this.save(this.formData);
        },
        afterSuccess({ data }) {
            this.formData = {};
            this.$emit('afterSubmitSuccess');
            $('#customer-modal').modal('hide');
            this.$emit('input', false);
            this.toastAndReload(data.message, 'customer-table');
        },
        afterSuccessFromGetEditData(response) {
            this.preloader = false;
            this.formData = response.data;
            if (Array.isArray(this.formData.linked_patients)) {
                this.formData.linked_patients = this.formData.linked_patients.filter(Boolean).join(', ');
            }
            if (this.formData.addresses.length) this.inputAddressRow = this.formData.addresses.slice(0, 2);
            if (this.formData.contacts.email.length) this.inputEmailRow = this.formData.contacts.email.map((item) => ({
                email: item.value,
                email_type: item.type
            })
            );
            if (this.formData.contacts.phone_number.length) this.inputPhoneNumberRow = this.formData.contacts.phone_number.map((item) => ({
                phone_number: item.value,
                country_code: item.type || '+91'
            })
            );
            if (!this.inputEmailRow.length) this.inputEmailRow = [{}];
            if (!this.inputPhoneNumberRow.length) this.inputPhoneNumberRow = [{ country_code: '+91' }];
            this.showSecondaryEmail = this.inputEmailRow.length > 1;
            this.showSecondaryPhone = this.inputPhoneNumberRow.length > 1;
        },
        setDefaultGroupId() {
            this.axiosGet(SELECTABLE_CUSTOMER_GROUP).then((response) => {
                let id;
                response.data.filter((item) => {
                    if (+item.is_default === 1) id = item.id;
                })
                this.formData.customer_group_id = id;
                this.customerGroupInputKey++;
            })
        },
        appendEmailRow() {
            let checkEmptyRows = this.inputEmailRow.filter(line => line.number === null);
            if (checkEmptyRows.length >= 1 && this.inputEmailRow.length > 0) return;
            this.inputEmailRow.push({});
        },
        removeEmailRow(rowId) {
            if (!this.removableEmailRow) this.inputEmailRow.splice(rowId, 1);
        },
        appendPhoneNumberRow() {
            let checkEmptyRows = this.inputPhoneNumberRow.filter(line => line.number === null);
            if (checkEmptyRows.length >= 1 && this.inputPhoneNumberRow.length > 0) return;
            this.inputPhoneNumberRow.push({});
        },
        removePhoneNumberRow(rowId) {
            if (!this.removablePhoneNumberRow) this.inputPhoneNumberRow.splice(rowId, 1);
        },
        removeAddressRow(rowId) {
            this.inputAddressRow.splice(rowId, 1);
        },
        addressHasData(index) {
            const a = this.inputAddressRow[index] || {};
            return !!(a.line_1 || a.line_2 || a.line_3 || a.city || a.zip_code);
        },
        appendAddressRow() {
            if (this.inputAddressRow.length >= 2) return;
            this.inputAddressRow.push({
                name: "na", city: "", country_id: "", zip_code: "", area: "",
                details: "", line_1: "", line_2: "", line_3: "", address_type: "",
            });
        },
    },
}
</script>

<style>
.required>label:first-child:after {
    color: #e32;
    content: ' *';
    display: inline;
}


/* Increase the width of the Select2 input */
.select2-container--default .select2-selection--single {
    width: 100%;
    border: 1px solid #ced4da;
}

/* Increase the height of the Select2 input */
.select2-container--default .select2-selection--single {
    height: 40px;
}

/* Increase padding and font size */
.select2-container--default .select2-selection--single .select2-selection__rendered {
    padding: 5px 10px;
    font-size: 16px;
}

.section-title {
    font-size: 1.4rem;
    font-weight: 700;
    color: #2c3e50;
}

/* Walk-in customers: only Prefix and First name are mandatory */
.walkin-mode .required:not(.always-required) > label:first-child:after {
    content: '';
}

/* B2B patients: only the b2b-required fields are mandatory */
.b2b-mode .required:not(.b2b-required) > label:first-child:after {
    content: '';
}
</style>